import { replaceMaterialSnippets } from '#imports';
import { Color, MeshPhysicalMaterial } from 'three';

import {
    shaderChunks as ShaderChunksFresnel,
    uniforms as uniformsFresnel,
} from '../shaders/ShaderChunks/fresnel';
import {
    shaderChunks as ShaderChunksMatcap,
    uniforms as uniformsMatcap,
} from '../shaders/ShaderChunks/matcap';
import glslBlend from '@/libs/lygia/color/blend.glsl';

export const createPackSceneObjectMaterial = (props = {}, uniforms = {}) => {
    const material = new MeshPhysicalMaterial(props);
    material.name = 'PackSceneObjectMaterial';
    // material.transparent = true;
    material.defines.USE_UV = '';

    replaceMaterialSnippets({
        material,
        uniforms: {
            ...uniformsFresnel(),
            ...uniformsMatcap(),
            uUseMatcap: { value: false },
            uNormalMapRepeat: { value: 1 },
            uFresnel: { value: 0.0 },
            uFresnelPower: { value: 5.0 },
            uFresnelColor: { value: new Color('red') },
            uMaskMap: { value: null },
            uMaskColor: { value: new Color('blue') },
            uOverride: { value: false },
            uMaskMetalnessFactor: { value: 0.0 },
            uMaskRoughnessFactor: { value: 0.0 },
            uUseMaskColor: { value: true },
            uOverlayColor: { value: new Color('blue') },
            uOverlayStr: { value: 0.5 },
            uFresnelStr: { value: 1.0 },
            uAlpha: { value: 1.0 },
            ...uniforms,
        },
        vertex: [
            {
                snippet: '#include <common>',
                newSnippet: vertexCommonReplaceSnippet,
            },
            {
                snippet: '#include <uv_vertex>',
                newSnippet: uvReplaceSnippet,
            },
        ],
        fragment: [
            {
                snippet: '#include <common>',
                newSnippet: fragmentCommonReplaceSnippet,
            },
            {
                snippet: '#include <map_fragment>',
                newSnippet: fragmentMapReplaceSnippet,
            },
            {
                snippet: '#include <metalnessmap_fragment>',
                newSnippet: fragmentMetalnessMapReplaceSnippet,
                insert: 'replace',
            },
            {
                snippet: '#include <roughnessmap_fragment>',
                newSnippet: fragmentRoughnessMapReplaceSnippet,
                insert: 'replace',
            },
            {
                snippet: '#include <opaque_fragment>',
                newSnippet: fragmentOpaqueReplaceSnippet,
            },
        ],
    });

    return material;
};

const vertexCommonReplaceSnippet = /* glsl */ `
    uniform float uNormalMapRepeat;
`;

const uvReplaceSnippet = /* glsl */ `
    #ifdef USE_NORMALMAP
        vNormalMapUv *= uNormalMapRepeat;
    #endif
    #ifdef USE_BUMPMAP
        vBumpMapUv *= uNormalMapRepeat;
    #endif
`;

const fragmentCommonReplaceSnippet = /* glsl */ `
    uniform float uNormalMapRepeat;
    uniform bool uUseMatcap;
    uniform vec3 uFresnelColor;
    uniform sampler2D uMaskMap;
    uniform vec3 uMaskColor;
    uniform bool uOverride;
    uniform float uMaskMetalnessFactor;
    uniform float uMaskRoughnessFactor;
    uniform bool uUseMaskColor;
    uniform vec3 uOverlayColor;
    uniform float uOverlayStr;
    uniform float uFresnelStr;
    uniform float uAlpha;

    ${glslBlend}
    ${ShaderChunksMatcap.uniforms}
    ${ShaderChunksMatcap.funcs}
    ${ShaderChunksFresnel.uniforms}
    ${ShaderChunksFresnel.functions}
`;

const fragmentMapReplaceSnippet = /* glsl */ `
    #ifdef USE_MASK
        float mask = texture2D(uMaskMap, vUv).b;
        if (uUseMaskColor) {
            diffuseColor.rgb = mix(diffuseColor.rgb, uMaskColor, mask);
        }
    #endif
`;

const fragmentMetalnessMapReplaceSnippet = /* glsl */ `
    float metalnessFactor = metalness;
    #ifdef USE_MASK
        // if (uOverride) {
            metalnessFactor = mix(metalnessFactor, uMaskMetalnessFactor, mask);
        // }
    #elif defined(USE_METALNESSMAP)
        vec4 texelMetalness = texture2D( metalnessMap, vMetalnessMapUv );
        metalnessFactor *= texelMetalness.b;
    #endif
`;

const fragmentRoughnessMapReplaceSnippet = /* glsl */ `
    float roughnessFactor = roughness;
        
    #ifdef USE_MASK
        // if (uOverride) {
            roughnessFactor = mix(roughnessFactor, uMaskRoughnessFactor, mask);
        // }
    #elif defined(USE_ROUGHNESSMAP)
        vec4 texelRoughness = texture2D( roughnessMap, vRoughnessMapUv );
        roughnessFactor *= texelRoughness.g;
    #endif
`;

const fragmentOpaqueReplaceSnippet = /* glsl */ `
    vec3 mvPosition = vViewPosition;
    // mvPosition = rotate(mvPosition, u_matcapRotationWorld, vec3(0.0, 1.0, 0.0));

    vec3 viewDir = normalize(vViewPosition);
    float NoV = max(dot(normal, viewDir), 0.0);
    float f = fresnel(uFresnel, NoV, uFresnelPower);


    vec3 color = gl_FragColor.rgb;
    // color = texture2D(tMatcap, vUv).rgb;
    if (uUseMatcap) {
        float matcapMask = 1.0;

        #ifdef USE_MASK
            matcapMask = 1.0 - mask;
        #endif

        vec3 matcapColor = matcap(tMatcap, normal, mvPosition, false, 1.0, 1.0, 1.0, u_matcapRotation);
        color = mix(color, matcapColor, matcapMask);
    }

    color = blendOverlay(color, uOverlayColor, uOverlayStr);
    gl_FragColor.a *= uAlpha;
    // color = blendMultiply(color, uOverlayColor, uOverlayStr);
    
    if (uFresnelStr > 0.0) color = mix(color, uFresnelColor, f * uAlpha * uFresnelStr);
    if (uOverlayStr > 0.0) color = blendOverlay(color, uOverlayColor, uOverlayStr);

    gl_FragColor.a *= uAlpha;
    // color = blendMultiply(color, uOverlayColor, uOverlayStr);
    
    // color = blendScreen(color, uFresnelColor, f);
    // vec3 cMatcap0 = matcap(tMatcap, normal, mvPosition, false, 0., 1.0, color.a, u_matcapRotation);

    gl_FragColor.rgb = color;
    // gl_FragColor.rgb = color;
    // gl_FragColor.rgb = vec3(normal);
    // gl_FragColor.rgb = vec3(mask);
`;
